import React, { useState, useEffect, forwardRef } from 'react'
import styled from 'styled-components'
import { Medium, MqTablet } from 'streamr-ui/css/consts'
import useIsMounted from '~hooks/useIsMounted'

const CountdownBox = styled.div`
    background: rgba(0, 0, 0, 0.5);
    mix-blend-mode: multiply;
    border-radius: 4px;
    width: 100%;
    height: 96px;
    box-sizing: border-box;
    padding: 0 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    max-width: 368px;

    @media ${MqTablet} {
        padding: 0 28px;
    }
`

const Inner = styled.div`
    width: 100%;
    color: #ffffff;
`

const Title = styled.div`
    font-weight: ${Medium};
    font-size: 12px;
    margin-bottom: 8px;

    @media ${MqTablet} {
        font-size: 14px;
    }
`

const Part = styled.div``

const Value = styled.span`
    font-size: 28px;
    line-height: 30px;

    @media ${MqTablet} {
        font-size: 32px;
    }
`

const Unit = styled.span`
    margin-top: 6px;
    font-size: 8px;
    letter-spacing: 0.1em;
    text-transform: uppercase;

    @media ${MqTablet} {
        margin-top: 4px;
        font-size: 10px;
    }
`

const Number = styled(Part)`
    width: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: visible;
`

const Separator = styled.div`
    width: 6px;
    height: 32px;
    position: relative;

    &:after,
    &:before {
        content: '';
        position: absolute;
        height: 5px;
        width: 5px;
        border-radius: 50%;
        left: 50%;
        top: 50%;
        background-color: rgba(255, 255, 255, 0.5);
    }

    &:after {
        transform: translate(-50%, -130%);
    }

    &:before {
        transform: translate(-50%, 30%);
    }

    @media ${MqTablet} {
        &:after,
        &:before {
            height: 6px;
            width: 6px;
        }
    }
`

const Timer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    font-weight: ${Medium};
`

const addLeadingZeros = (value) => (value <= 9 && value >= 0 ? '0' : '') + Math.max(value, 0)

const UnstyledCountdown = forwardRef(function UnstyledCountdown(
    { targetDate, title, ...props },
    ref,
) {
    const isMounted = useIsMounted()
    const [{ days, hours, min, sec }, setDate] = useState({
        days: 0,
        hours: 0,
        min: 0,
        sec: 0,
    })

    useEffect(() => {
        const timeout = setInterval(() => {
            if (isMounted()) {
                const dateNow = Math.ceil(Date.now() / 1000) * 1000 // Round to the next second
                let diff = (targetDate.getTime() - dateNow) / 1000

                // clear countdown when date is reached
                if (diff < -1) {
                    clearTimeout(timeout)
                    return
                }

                const timeLeft = {
                    years: 0,
                    days: 0,
                    hours: 0,
                    min: 0,
                    sec: 0,
                    millisec: 0,
                }

                // calculate time difference between now and expected date
                if (diff >= 365.25 * 86400) {
                    // 365.25 * 24 * 60 * 60
                    timeLeft.years = Math.floor(diff / (365.25 * 86400))
                    diff -= timeLeft.years * 365.25 * 86400
                }
                if (diff >= 86400) {
                    // 24 * 60 * 60
                    timeLeft.days = Math.floor(diff / 86400)
                    diff -= timeLeft.days * 86400
                }
                if (diff >= 3600) {
                    // 60 * 60
                    timeLeft.hours = Math.floor(diff / 3600)
                    diff -= timeLeft.hours * 3600
                }
                if (diff >= 60) {
                    timeLeft.min = Math.floor(diff / 60)
                    diff -= timeLeft.min * 60
                }
                timeLeft.sec = Math.floor(diff)

                setDate({
                    days: timeLeft.days,
                    hours: timeLeft.hours,
                    min: timeLeft.min,
                    sec: timeLeft.sec,
                })
            }
        }, 250)

        return () => {
            clearTimeout(timeout)
        }
    }, [isMounted, targetDate])

    return (
        <div {...props} ref={ref}>
            <CountdownBox>
                <Inner>
                    <Title>{title}</Title>
                    <Timer>
                        <Number>
                            <Value>{addLeadingZeros(days)}</Value>
                            <Unit>Days</Unit>
                        </Number>
                        <Separator />
                        <Number>
                            <Value>{addLeadingZeros(hours)}</Value>
                            <Unit>Hours</Unit>
                        </Number>
                        <Separator />
                        <Number>
                            <Value>{addLeadingZeros(min)}</Value>
                            <Unit>Minutes</Unit>
                        </Number>
                        <Separator />
                        <Number>
                            <Value>{addLeadingZeros(sec)}</Value>
                            <Unit>Seconds</Unit>
                        </Number>
                    </Timer>
                </Inner>
            </CountdownBox>
        </div>
    )
})

const Countdown = styled(UnstyledCountdown)`
    margin: 0;
`

export default Countdown
